import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminDashboard';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Edit, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [displayMap, setDisplayMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingSession, setEditingSession] = useState(null);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/sessions`, { headers: getAuthHeader() });
      setSessions(response.data);
      setDisplayMap(Object.fromEntries(response.data.map(session => [
        session.id,
        {
          price_text: session.price_text || '',
          show_price: session.show_price !== false,
          note: session.note || ''
        }
      ])));
    } catch (error) {
      toast.error('Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (sessionData) => {
    try {
      const { price_text, show_price, note, ...sessionPayload } = sessionData;
      let savedSession;
      if (sessionData.id) {
        const response = await axios.put(
          `${API_URL}/admin/sessions/${sessionData.id}`,
          sessionPayload,
          { headers: getAuthHeader() }
        );
        savedSession = response.data;
        toast.success('Session updated successfully');
      } else {
        const response = await axios.post(
          `${API_URL}/admin/sessions`,
          sessionPayload,
          { headers: getAuthHeader() }
        );
        savedSession = response.data;
        toast.success('Session created successfully');
      }

      const nextDisplayMap = {
        ...displayMap,
        [savedSession.id]: {
          price_text: price_text || '',
          show_price: show_price !== false,
          note: note || ''
        }
      };
      await axios.put(
        `${API_URL}/admin/settings/session_display`,
        { value: nextDisplayMap },
        { headers: getAuthHeader() }
      );
      setDisplayMap(nextDisplayMap);
      setEditingSession(null);
      fetchSessions();
    } catch (error) {
      toast.error('Failed to save session');
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/sessions/${sessionId}`, {
        headers: getAuthHeader()
      });
      toast.success('Session deleted successfully');
      fetchSessions();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  if (loading) return <AdminLayout title="Sessions"><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout title="Manage Sessions">
      <div className="space-y-6">
        <Button
          onClick={() => setEditingSession({ name: '', duration: '', price: 0, description: '', active: true, price_text: '', show_price: true, note: '' })}
          className="bg-sage-700 hover:bg-sage-800 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New Session
        </Button>

        <div className="grid md:grid-cols-2 gap-6">
          {sessions.map((session) => (
            <Card key={session.id} className="border-0 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{session.name}</h3>
                    <p className="text-sm text-gray-600">{session.duration}</p>
                  </div>
                  <div className="text-right">
                    <span className="block text-2xl font-bold text-sage-700">{session.show_price === false ? 'Hidden' : (session.price_text || `$${session.price}`)}</span>
                    <span className={`text-xs font-semibold ${session.active ? 'text-sage-700' : 'text-gray-400'}`}>{session.active ? 'Visible' : 'Hidden from site'}</span>
                  </div>
                </div>
                <p className="text-gray-700">{session.description}</p>
                <div className="flex space-x-2 pt-4">
                  <Button
                    onClick={() => setEditingSession(session)}
                    variant="outline"
                    className="flex-1 border-sage-300 text-sage-700"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(session.id)}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-700"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {editingSession && (
        <SessionEditModal
          session={editingSession}
          onSave={handleSave}
          onClose={() => setEditingSession(null)}
        />
      )}
    </AdminLayout>
  );
};

const SessionEditModal = ({ session, onSave, onClose }) => {
  const [formData, setFormData] = useState({ active: true, show_price: true, price_text: '', note: '', ...session });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl">
        <CardContent className="p-8">
          <h3 className="text-2xl font-serif mb-6">{session.id ? 'Edit Session' : 'New Session'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Duration</label>
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Price</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                required
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Public Price Text</label>
              <input
                type="text"
                value={formData.price_text || ''}
                onChange={(e) => setFormData({ ...formData, price_text: e.target.value })}
                placeholder="$0, Free consultation, Call for pricing, etc."
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex items-center space-x-3 rounded-lg border-2 border-sage-100 p-3">
                <input
                  type="checkbox"
                  checked={formData.show_price !== false}
                  onChange={(e) => setFormData({ ...formData, show_price: e.target.checked })}
                  className="h-5 w-5 accent-sage-700"
                />
                <span className="text-sm font-medium">Show public price</span>
              </label>
              <label className="flex items-center space-x-3 rounded-lg border-2 border-sage-100 p-3">
                <input
                  type="checkbox"
                  checked={formData.active !== false}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="h-5 w-5 accent-sage-700"
                />
                <span className="text-sm font-medium">Show this session</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={4}
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Extra Note (Optional)</label>
              <textarea
                value={formData.note || ''}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                rows={2}
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none"
                placeholder="Short note shown under the session description"
              />
            </div>
            <div className="flex space-x-4 pt-4">
              <Button type="submit" className="flex-1 bg-sage-700 hover:bg-sage-800 text-white">
                Save
              </Button>
              <Button type="button" onClick={onClose} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSessions;
